import { useQuery } from '@tanstack/react-query';
import { snowflakeApi } from '../utils/api';

// Service implementation
export class BlobService {
  private static instance: BlobService;

  private constructor() {}

  static getInstance(): BlobService {
    if (!BlobService.instance) {
      BlobService.instance = new BlobService();
    }
    return BlobService.instance;
  }

  // Get blob files from all modules
  public async getAllBlobFiles(workflowId: string, clientId: string): Promise<{files: string[], isMockData: boolean}> {
    console.log(`Getting all blob files for workflow ${workflowId} and client ${clientId}`);
    
    // Try different API endpoints to find files
    const tryApis = async () => {
      try {
        // Try Module 3 API first using the storage endpoint
        const response = await snowflakeApi.get('/api/v1/storage/list', {
          params: { 
            prefix: `${clientId}/workflows/${workflowId}/`,
            container: 'client-data',
            client_id: clientId
          }
        });
        
        console.log('Successfully retrieved files from Snowflake BFF');
        
        // Check response format and extract file paths
        let fileList: string[] = [];
        if (response.data) {
          if (Array.isArray(response.data.blobs)) {
            fileList = response.data.blobs;
          } else if (Array.isArray(response.data.files)) {
            // If files is an array of objects with a path property
            fileList = response.data.files.map((file: any) => 
              typeof file === 'string' ? file : (file.path || file.name || '')
            );
          } else if (Array.isArray(response.data)) {
            // If the response itself is an array
            fileList = response.data.map((item: any) => 
              typeof item === 'string' ? item : (item.path || item.name || '')
            );
          }
        }
        
        return {
          files: fileList,
          isMockData: false
        };
      } catch (error) {
        console.error('Error getting blob files from Module 3:', error);
        
        // Fall back to Module 1 API
        try {
          const response = await snowflakeApi.get('/api/v1/storage/list', {
            params: { 
              prefix: `${clientId}/workflows/${workflowId}/`,
              container: 'client-data',
              client_id: clientId
            }
          });
          
          console.log('Successfully retrieved files from snowflakeApi');
          console.log('Module1 API response:', response.data);
          
          // Check response format and extract file paths
          let fileList: string[] = [];
          if (response.data) {
            if (Array.isArray(response.data.blobs)) {
              fileList = response.data.blobs;
            } else if (Array.isArray(response.data.files)) {
              // If files is an array of objects with a path property
              fileList = response.data.files.map((file: any) => 
                typeof file === 'string' ? file : (file.path || file.name || '')
              );
            } else if (Array.isArray(response.data)) {
              // If the response itself is an array
              fileList = response.data.map((item: any) => 
                typeof item === 'string' ? item : (item.path || item.name || '')
              );
            }
          }
          
          return {
            files: fileList,
            isMockData: false
          };
        } catch (error1) {
          console.error('Error getting blob files from Module 1:', error1);
          
          // Last attempt with Module 2 API
          try {
            const response = await snowflakeApi.get('/api/v1/module2/storage/list', {
              params: { 
                prefix: `${clientId}/workflows/${workflowId}/`,
                container: 'client-data',
                client_id: clientId
              }
            });
            
            console.log('Successfully retrieved files from snowflakeApi');
            
            // Check response format and extract file paths
            let fileList: string[] = [];
            if (response.data) {
              if (Array.isArray(response.data.blobs)) {
                fileList = response.data.blobs;
              } else if (Array.isArray(response.data.files)) {
                // If files is an array of objects with a path property
                fileList = response.data.files.map((file: any) => 
                  typeof file === 'string' ? file : (file.path || file.name || '')
                );
              } else if (Array.isArray(response.data)) {
                // If the response itself is an array
                fileList = response.data.map((item: any) => 
                  typeof item === 'string' ? item : (item.path || item.name || '')
                );
              }
            }
            
            return {
              files: fileList,
              isMockData: false
            };
          } catch (error2) {
            console.error('Error getting blob files from Module 2:', error2);
            throw new Error('Failed to get files from any module API');
          }
        }
      }
    };
    
    try {
      return await tryApis();
    } catch (error) {
      console.error('All API attempts failed:', error);
      
      // Fallback to mock response
      return {
        files: [
          `${clientId}/workflows/${workflowId}/module1/topics.json`,
          `${clientId}/workflows/${workflowId}/module2/executions/latest/relevant.csv`,
          `${clientId}/workflows/${workflowId}/module2/executions/latest/undecided.csv`,
          `${clientId}/workflows/${workflowId}/module1/sample_topics.json`, // Add sample data indicators
          `${clientId}/workflows/${workflowId}/module2/executions/latest/sample_relevant.csv`,
          `${clientId}/workflows/${workflowId}/module2/output/framework.json`,
        ],
        isMockData: true
      };
    }
  }
}

// React Query hook for getting blob files
export const useGetBlobFiles = (workflowId: string, clientId: string) => {
  return useQuery({
    queryKey: ['blobFiles', workflowId, clientId],
    queryFn: () => BlobService.getInstance().getAllBlobFiles(workflowId, clientId),
    enabled: !!workflowId && !!clientId
  });
}; 